import React, {
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import AppContext from "src/components/AppContext";
import { VerifiedMobileInput } from "src/components/users/VerifiedMobileInput";
import { alertService, userService } from "src/services";
import {
  ConfirmationResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  User,
} from "firebase/auth";
import { auth } from "helpers/firebase";
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  getRedirectResult,
} from "firebase/auth";
import LoginBg from "src/assets/images/LoginBg.png";
import LogoWhite from "public/images/logo_white.png";
import Image from "next/image";
import { fetchWrapper } from "src/helpers";
import { FirebaseError } from "firebase/app";
import { getCmsSubscriptionRedirectPath } from "src/helpers/coupon-client";
import FooterCopyright from "src/common/FooterCopyright";

interface ContactData {
  mobile_number: string;
}

const UserLogin = () => {
  const context = useContext(AppContext);
  const [data, setData] = useState<ContactData>({
    mobile_number: "",
  });
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [resendCount, setResendCount] = useState<number>(0);
  const [confirmResult, setConfirmResult] = useState<ConfirmationResult>();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [showFullPolicy, setShowFullPolicy] = useState(false);
  const [isSocialLoading, setIsSocialLoading] = useState(false);
  const [socialLoadingType, setSocialLoadingType] = useState<
    "google" | "apple" | null
  >(null);
  const [captchaVerified, setCaptchaVerified] = useState<boolean>(false);
  const { setShowLoader } = context.state;

  const authHeaders = React.useMemo<Record<string, string>>(() => {
    if (typeof window === "undefined") return { Authorization: "" };
    const [AUTH_USER, AUTH_PASS] = (
      process.env.NEXT_PUBLIC_BASIC_AUTH || ":"
    ).split(":");
    const token = `${AUTH_USER}:${AUTH_PASS}`;
    const encodedToken = btoa(token);
    return { Authorization: "Basic " + encodedToken };
  }, []);
  const [showTosCheckbox, setShowTosCheckbox] = useState<boolean>(false);
  const [tosChecked, setTosChecked] = useState<boolean>(false);
  const tosRef = useRef<HTMLInputElement>(null);
  const [highlightTos, setHighlightTos] = useState(false);

  const [showDeletionModal, setShowDeletionModal] = useState<boolean>(false);
  const [deletionData, setDeletionData] = useState<{
    token: string;
    status: string | null;
    message: string;
    user: User | null;
    loginMethod: "google" | "apple" | "mobile" | null;
  }>({
    token: "",
    status: null,
    message: "",
    user: null,
    loginMethod: null,
  });

  const [isMounted, setIsMounted] = useState(false);

  const logLoginError = useCallback(
    async (error: unknown, email?: string | null, phone?: string | null) => {
      try {
        if (error instanceof FirebaseError) {
          const ignoredErrors = [
            "auth/popup-closed-by-user",
            "auth/popup-blocked",
            "auth/cancelled-popup-request",
            "auth/user-cancelled",
            "auth/invalid-phone-number",
            "auth/missing-phone-number",
          ];
          if (ignoredErrors.includes(error.code)) {
            return;
          }
        }

        const errorCode =
          error instanceof FirebaseError ? error.code : "UNKNOWN_ERROR";
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        const payload: Record<string, string> = {
          error_message: errorMessage,
          error_code: errorCode,
        };
        if (email) payload.email = email;
        if (phone) payload.mobile_number = phone;

        await fetchWrapper.post("/api/users/error_log", payload, authHeaders);
      } catch (e) {
        console.error("Failed to save error log:", e);
      }
    },
    [authHeaders],
  );

  const handleCancelDeletion = async (action: string) => {
    if (action === "Yes") {
      try {
        if (
          deletionData.loginMethod === "google" ||
          deletionData.loginMethod === "apple"
        ) {
          setIsSocialLoading(true);
          setSocialLoadingType(deletionData.loginMethod);
        } else if (deletionData.loginMethod === "mobile") {
          setIsLoading(true);
        }
        const tokenHeader = { Authorization: `Bearer ${deletionData.token}` };
        if (deletionData.status === "PENDING") {
          await fetchWrapper.delete(
            "/api/profile/request-deletion",
            undefined,
            tokenHeader,
          );
        } else {
          await fetchWrapper.put(
            "/api/profile/request-deletion",
            undefined,
            tokenHeader,
          );
        }
        alertService.success(
          deletionData.status === "PENDING"
            ? "Deletion process cancelled successfully. Logging you in..."
            : "Account reactivated successfully. Logging you in...",
        );
        setShowDeletionModal(false);
        if (deletionData.user) {
          void handleFormSubmit(deletionData.user);
        }
      } catch (error) {
        alertService.error(
          typeof error === "string"
            ? error
            : "Failed to cancel deletion process.",
        );
      } finally {
        setIsSocialLoading(false);
        setSocialLoadingType(null);
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);
  useEffect(() => {
    const checkRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          console.log(
            "Successfully retrieved user from redirect:",
            result.user.email,
          );
          localStorage.setItem("isLoggedInWith", "Google-Redirect");
          await handleFormSubmit(result.user);
        }
      } catch (error) {
        console.error("Error during redirect sign-in result check: ", error);
        await logLoginError(error);
      }
    };
    checkRedirectResult();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: "select_account",
    });
    try {
      setIsSocialLoading(true);
      setSocialLoadingType("google");
      if (setShowLoader) setShowLoader(true);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      console.log("Google sign-in successful:", user.email);
      localStorage.setItem("isLoggedInWith", "Google");
      await handleFormSubmit(user);
    } catch (error) {
      console.error("Error during Google sign-in process:", error);
      if (error instanceof FirebaseError) {
        let errorMessage = "Something went wrong. Please try again later.";

        if (error.code === "auth/popup-closed-by-user") {
          errorMessage = "Google sign-in canceled by user.";
        } else if (error.code === "auth/popup-blocked") {
          errorMessage =
            "Login popup was blocked by your browser. Please allow popups or try again.";
        }
        console.log("Firebase Auth Error:", error.code, error.message);
        alertService.error(errorMessage);
        await logLoginError(error);
      } else {
        alertService.error(
          "An unexpected error occurred during Google sign-in.",
        );
        await logLoginError(error);
      }
    } finally {
      setIsSocialLoading(false);
      setSocialLoadingType(null);
      if (setShowLoader) setShowLoader(false);
    }
  };

  const handleAppleSignIn = async () => {
    const provider = new OAuthProvider("apple.com");
    try {
      setIsSocialLoading(true);
      setSocialLoadingType("apple");
      if (setShowLoader) setShowLoader(true);
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      console.log("Apple sign-in successful:", user.email || user.uid);
      localStorage.setItem("isLoggedInWith", "Apple");
      await handleFormSubmit(user);
    } catch (error) {
      console.error("Error during Apple sign-in process:", error);
      if (error instanceof FirebaseError) {
        let errorMessage = "Something went wrong. Please try again later.";

        if (error.code === "auth/popup-closed-by-user") {
          errorMessage = "Apple sign-in canceled by user.";
        } else if (error.code === "auth/popup-blocked") {
          errorMessage =
            "Login popup was blocked by your browser. Please allow popups or try again.";
        }
        console.log("Firebase Auth Error (Apple):", error.code, error.message);
        alertService.error(errorMessage);
        await logLoginError(error);
      } else {
        alertService.error(
          "An unexpected error occurred during Apple sign-in.",
        );
        await logLoginError(error);
      }
    } finally {
      setIsSocialLoading(false);
      setSocialLoadingType(null);
      if (setShowLoader) setShowLoader(false);
    }
  };

  const handleFormSubmit = async (user: User) => {
    try {
      setShowLoader?.(true);
      console.log(
        "Submitting login form for user:",
        user.email || user.phoneNumber,
      );

      const loggedInUser = await userService.userLogin(user);
      const { token } = loggedInUser || {};
      if (!token) {
        console.error("Login failed: Backend did not return a token");
        throw new Error("Login failed: no token received");
      }

      console.log(
        "Login successful, received token for:",
        loggedInUser.email || loggedInUser.customer_email,
      );
      const fcmToken = localStorage.getItem("fcm_token");
      if (fcmToken) {
        await userService.syncFcmToken(fcmToken, token);
      }

      const referer = localStorage.getItem("referer");
      localStorage.removeItem("referer");

      const hasPlan = loggedInUser.is_subscribed || loggedInUser.is_free;
      const cmsSubscriptionPath = !hasPlan
        ? getCmsSubscriptionRedirectPath()
        : null;
      const allowedPaths = [
        "/profile",
        "/subscription",
        "/payment-success",
        "/support",
        "/testimonials",
        "/refund-policy",
        "/shipping-and-delivery-policy",
        "/api-docs",
      ];
      const isRefererAllowed =
        referer && allowedPaths.some((p) => referer.startsWith(p));

      if (cmsSubscriptionPath) {
        console.log("Redirecting to CMS checkout:", cmsSubscriptionPath);
        window.location.href = cmsSubscriptionPath;
      } else if (referer && (hasPlan || isRefererAllowed)) {
        console.log("Redirecting to referer:", referer);
        window.location.href = referer;
      } else {
        console.log("No valid referer or no plan, reloading page");
        window.location.reload();
      }
    } catch (error) {
      console.error("Error in handleFormSubmit:", error);
      const err = error as {
        is_deleted?: boolean;
        deletion_request_status?: string | null;
        message?: string;
      };
      if (err?.is_deleted) {
        try {
          const idToken = await user.getIdToken();
          const loggedInWith = localStorage.getItem("isLoggedInWith");
          const method =
            loggedInWith === "Google"
              ? "google"
              : loggedInWith === "Apple"
                ? "apple"
                : "mobile";

          setDeletionData({
            token: idToken,
            status: err.deletion_request_status || null,
            message: err.message || "This account cannot be accessed.",
            user: user,
            loginMethod: method,
          });
          setShowDeletionModal(true);
        } catch (e) {
          console.error("Error getting ID token:", e);
        }
        return;
      }
      alertService.error(
        typeof error === "string"
          ? error
          : "Something went wrong during login. Please try again later.",
      );
      await logLoginError(error, user.email, user.phoneNumber);
    } finally {
      setShowLoader?.(false);
    }
  };

  const handleSendOtp = async (
    e?: FormEvent<HTMLFormElement>,
    number?: string,
  ) => {
    if (e) e.preventDefault();

    const phone = number || data.mobile_number;
    if (!/^\d{10}$/.test(phone)) {
      setErrorMessage("Please enter a valid 10-digit mobile number.");
      return;
    }

    try {
      const res = await fetchWrapper.post(
        "/api/users/verifyMobile",
        {
          mobile_number: `+91${phone}`,
        },
        authHeaders,
      );

      if (res?.success) {
        setShowTosCheckbox(!res.tos_accepted);
        await handleSendCode();
      } else {
        const message =
          res?.message || "Mobile verification failed. Please try again.";
        setErrorMessage(message);
        // alertService.error(message);
      }
    } catch (err) {
      const error = err as string;
      const message = error || "Failed to send OTP. Please try again.";
      setErrorMessage(message);
      // alertService.error(message);
    }
  };

  const setRecaptcha = useCallback(async () => {
    try {
      if (!window.recaptchaVerifier) {
        const recaptchaContainer = document.getElementById(
          "recaptcha-container",
        );
        if (!recaptchaContainer) {
          console.error("Recaptcha container not found");
          return;
        }

        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          {
            size: "invisible",
            callback: () => {
              console.log("Recaptcha verified.");
              setCaptchaVerified(true);
            },
            "expired-callback": () => {
              console.warn("Recaptcha expired. Resetting...");
              window.recaptchaVerifier = null;
              setRecaptcha(); // Reinitialize
            },
          },
        );

        if (!captchaVerified) await window.recaptchaVerifier.render();
      }
    } catch (error) {
      console.error("Error initializing Recaptcha", error);
    }
  }, [captchaVerified]);

  const handleSendCode = useCallback(async () => {
    setIsLoading(true);
    if (setShowLoader) setShowLoader(true);

    const recaptchaElement = document.getElementById("recaptcha-container");

    try {
      if (recaptchaElement) {
        recaptchaElement.classList.add("mt-2");
        recaptchaElement.classList.add("d-block");
      }

      await setRecaptcha();

      const appVerifier = window.recaptchaVerifier;

      const confirmationResult = await signInWithPhoneNumber(
        auth,
        `+91${data.mobile_number}`,
        appVerifier,
      );
      alertService.success("OTP sent successfully!");
      setConfirmResult(confirmationResult);
      setResendTimer(30);
      setResendCount((prev) => prev + 1);
    } catch (error: unknown) {
      console.error("Firebase signInWithPhoneNumber error:", error);

      if (error instanceof FirebaseError) {
        let errorMessage = "Something went wrong. Please try again later.";

        if (
          error.message.includes("invalid-app-credential") ||
          error.message.includes("invalid-phone-number")
        ) {
          errorMessage = "Please enter a valid mobile number.";
          if (recaptchaElement) {
            recaptchaElement.classList.add("mt-2");
            recaptchaElement.classList.add("d-none");
            recaptchaElement.classList.remove("d-block");
          }
        } else {
          errorMessage =
            "Something went wrong with captcha. Please try again later.";
          if (window.recaptchaVerifier) {
            window.recaptchaVerifier.clear();
            window.recaptchaVerifier = null;
          }
          if (recaptchaElement) {
            recaptchaElement.classList.add("mt-2", "d-none");
            recaptchaElement.classList.remove("d-block");
          }
        }
        setErrorMessage(errorMessage);
        await logLoginError(error, undefined, data.mobile_number);
      } else {
        setErrorMessage("Something went wrong. Please try again later.");
        await logLoginError(error, undefined, data.mobile_number);
      }
    } finally {
      if (setShowLoader) setShowLoader(false);
      setIsLoading(false);
    }
  }, [
    setShowLoader,
    data.mobile_number,
    setConfirmResult,
    setRecaptcha,
    setResendCount,
    logLoginError,
  ]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-black">
      {/* Background Image */}
      {!isMounted ? (
        // Optional: A simple loading spinner or matching background color
        <div className="bg-black" />
      ) : (
        <>
          <div
            className="fixed inset-0 bg-cover bg-center z-0"
            style={{
              backgroundImage: `url(${LoginBg.src})`,
              // opacity: 0.2,
            }}
          />

          {/* Glass Card */}
          <div className="relative z-10 w-full max-w-md">
            <div className="bg-gray-900/80 backdrop-blur-lg rounded-2xl shadow-2xl overflow-hidden border border-gray-800">
              {/* Logo and Welcome Text */}
              <div className="p-8 text-center">
                <div className="flex flex-col items-center mb-6">
                  <div className="flex justify-center mb-4 h-10">
                    <Image
                      src={LogoWhite}
                      alt="ABillion Logo"
                      className="h-full w-auto object-contain object-left"
                    />
                  </div>
                  <div className="text-sm font-medium text-gray-400 mb-2">
                    Invest Smart, Stay Ahead
                  </div>
                  <h1 className="text-3xl font-bold text-white mb-2">
                    Welcome to ABillion!
                  </h1>
                  <p className="text-gray-300 mb-6">
                    Your gateway to investing smarter,
                    <br />
                    profit for everyone starts here!
                  </p>
                </div>

                <div className="space-y-6">
                  <VerifiedMobileInput
                    setShowLoader={setShowLoader}
                    onVerificationSuccess={handleFormSubmit}
                    setRecaptcha={setRecaptcha}
                    handleSendOtp={handleSendOtp}
                    setErrorMessage={setErrorMessage}
                    resendTimer={resendTimer}
                    setResendTimer={setResendTimer}
                    confirmResult={confirmResult}
                    setConfirmResult={setConfirmResult}
                    resendCount={resendCount}
                    errorMessage={errorMessage}
                    isLoading={isLoading}
                    isSocialLoading={isSocialLoading}
                    setData={setData}
                    data={data}
                    tosAccepted={tosChecked}
                    showTosCheckbox={showTosCheckbox}
                    setHighlightTos={setHighlightTos}
                    tosRef={tosRef}
                    label="Mobile number"
                    placeholder="Enter your registered Mobile number"
                  />

                  {!confirmResult && (
                    <>
                      <div className="relative flex items-center my-6">
                        <div className="flex-grow border-t border-gray-700"></div>
                        <span className="flex-shrink mx-4 text-gray-400 text-sm">
                          Or continue with
                        </span>
                        <div className="flex-grow border-t border-gray-700"></div>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <button
                          type="button"
                          onClick={handleGoogleSignIn}
                          disabled={isSocialLoading || isLoading}
                          className={`flex items-center justify-center gap-2 w-full border border-gray-700 hover:bg-gray-800 text-white font-medium py-2 px-4 rounded-lg transition duration-200 ${
                            isSocialLoading || isLoading
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                        >
                          {socialLoadingType === "google" ? (
                            <svg
                              className="animate-spin h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          ) : (
                            <svg
                              width="19"
                              height="29"
                              viewBox="0 0 25 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g clipPath="url(#clip0_4204_534)">
                                <path
                                  d="M24.2663 12.2763C24.2663 11.4605 24.2001 10.6404 24.059 9.83789H12.7402V14.4589H19.222C18.953 15.9492 18.0888 17.2676 16.8233 18.1054V21.1037H20.6903C22.9611 19.0137 24.2663 15.9272 24.2663 12.2763Z"
                                  fill="#4285F4"
                                ></path>
                                <path
                                  d="M12.7401 24.0013C15.9766 24.0013 18.7059 22.9387 20.6945 21.1044L16.8276 18.106C15.7517 18.838 14.3627 19.2525 12.7445 19.2525C9.61388 19.2525 6.95946 17.1404 6.00705 14.3008H2.0166V17.3917C4.05371 21.4439 8.2029 24.0013 12.7401 24.0013Z"
                                  fill="#34A853"
                                ></path>
                                <path
                                  d="M6.00277 14.3007C5.50011 12.8103 5.50011 11.1965 6.00277 9.70618V6.61523H2.01674C0.314734 10.006 0.314734 14.0009 2.01674 17.3916L6.00277 14.3007Z"
                                  fill="#FBBC04"
                                ></path>
                                <path
                                  d="M12.7401 4.74966C14.4509 4.7232 16.1044 5.36697 17.3434 6.54867L20.7695 3.12262C18.6001 1.0855 15.7208 -0.034466 12.7401 0.000808666C8.2029 0.000808666 4.05371 2.55822 2.0166 6.61481L6.00264 9.70575C6.95064 6.86173 9.60947 4.74966 12.7401 4.74966Z"
                                  fill="#EA4335"
                                ></path>
                              </g>
                              <defs>
                                <clipPath id="clip0_4204_534">
                                  <rect
                                    width="24"
                                    height="24"
                                    fill="white"
                                    transform="translate(0.5)"
                                  ></rect>
                                </clipPath>
                              </defs>
                            </svg>
                          )}
                          Continue with Google
                        </button>
                        <button
                          type="button"
                          onClick={handleAppleSignIn}
                          disabled={isSocialLoading || isLoading}
                          className={`flex items-center justify-center gap-2 w-full border border-gray-700 hover:bg-gray-800 text-white font-medium py-2.5 px-4 rounded-lg transition duration-200 ${
                            isSocialLoading || isLoading
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                        >
                          {socialLoadingType === "apple" ? (
                            <svg
                              className="animate-spin h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="120 90 420 460"
                              width="25"
                              height="25"
                              fill="currentColor"
                              className="text-white"
                            >
                              <path d="M447.1 332.7C446.9 296 463.5 268.3 497.1 247.9C478.3 221 449.9 206.2 412.4 203.3C376.9 200.5 338.1 224 323.9 224C308.9 224 274.5 204.3 247.5 204.3C191.7 205.2 132.4 248.8 132.4 337.5C132.4 363.7 137.2 390.8 146.8 418.7C159.6 455.4 205.8 545.4 254 543.9C279.2 543.3 297 526 329.8 526C361.6 526 378.1 543.9 406.2 543.9C454.8 543.2 496.6 461.4 508.8 424.6C443.6 393.9 447.1 334.6 447.1 332.7zM390.5 168.5C417.8 136.1 415.3 106.6 414.5 96C390.4 97.4 362.5 112.4 346.6 130.9C329.1 150.7 318.8 175.2 321 202.8C347.1 204.8 370.9 191.4 390.5 168.5z" />
                            </svg>
                          )}
                          Continue with Apple
                        </button>
                      </div>

                      <div className="text-center ">
                        <a
                          href={process.env.NEXT_PUBLIC_HELP_LINK || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group inline-flex items-center text-sm font-medium text-green-500 hover:text-green-400 transition-colors duration-200"
                        >
                          Get Support
                        </a>
                      </div>
                    </>
                  )}

                  {/* New Policy Checkbox Code */}
                  {showTosCheckbox && confirmResult ? (
                    <div className="flex flex-col items-start mb-4 w-full">
                      <div className="flex items-start mb-2">
                        <input
                          ref={tosRef}
                          id="terms"
                          name="terms"
                          type="checkbox"
                          checked={tosChecked}
                          onChange={(e) => setTosChecked(e.target.checked)}
                          className={`h-4 w-4 accent-green-400 rounded border-green-400 mt-1 transition-all duration-300 ${
                            highlightTos ? "ring-2 " : ""
                          }`}
                        />
                        <label
                          htmlFor="terms"
                          className="block text-sm text-gray-300 ml-2 text-justify"
                        >
                          {showFullPolicy ? (
                            <>
                              By signing up, you agree to share your contact
                              details and information to create an account and
                              improve user experience. You&apos;re always in
                              control — manage or delete your data anytime. By
                              creating an account, you agree to our{" "}
                              <a
                                href="https://www.abillionanalytics.com/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-400 hover:text-green-300 text-sm font-medium cursor-pointer"
                              >
                                Privacy Policy
                              </a>{" "}
                              and{" "}
                              <a
                                href="https://www.abillionanalytics.com/terms-and-conditions"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-400 hover:text-green-300 text-sm font-medium cursor-pointer"
                              >
                                Terms of Use
                              </a>
                              , and review these two policies for more details.
                            </>
                          ) : (
                            <>
                              By signing up, you agree to share your contact
                              details and information to create an account and
                              ...
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => setShowFullPolicy(!showFullPolicy)}
                            className="text-green-400 hover:text-green-300 text-sm font-medium cursor-pointer ml-1"
                          >
                            {showFullPolicy ? "Read less" : "Read more"}
                          </button>
                        </label>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-white mb-4">
                      By creating an account you agree to our
                      <br />
                      <a
                        href="https://www.abillionanalytics.com/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-400 hover:text-green-300 text-sm font-medium cursor-pointer ml-1"
                      >
                        Privacy Policy
                      </a>
                      &nbsp;and
                      <a
                        href="https://www.abillionanalytics.com/terms-and-conditions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-400 hover:text-green-300 text-sm font-medium cursor-pointer ml-1"
                      >
                        Terms of Use.
                      </a>
                    </div>
                  )}
                </div>
                {/* Footer */}
                <FooterCopyright
                  variant="stacked"
                  className="mt-6 text-center text-xs sm:text-sm text-gray-500 space-y-1"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {showDeletionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-gray-900/90 backdrop-blur-lg rounded-2xl shadow-2xl border border-gray-800 w-full max-w-md p-6 relative overflow-hidden">
            <h2 className="text-2xl font-bold text-white mb-2">
              Account Inaccessible
            </h2>
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700 mb-6 text-sm text-gray-300">
              {deletionData.status === "PENDING"
                ? "Your account is scheduled for deletion. Would you like to reactivate your account and cancel the deletion request? Your data will be preserved."
                : "Your account has been deleted, and your data has been permanently removed. Would you like to reactivate your account?"}
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                className="cursor-pointer px-5 py-2.5 rounded-lg font-medium text-gray-400 hover:text-white hover:bg-gray-800 transition-colors border border-gray-700"
                onClick={() => setShowDeletionModal(false)}
              >
                No, leave it
              </button>
              <button
                className="cursor-pointer px-5 py-2.5 rounded-lg font-medium text-black bg-green-500 hover:bg-green-400 transition-colors shadow-lg shadow-green-900/20"
                onClick={() => handleCancelDeletion("Yes")}
              >
                Yes, Reactivate Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(UserLogin);
